<template>
  <div class="app">
    <header class="topbar">
      <div class="topbar-inner">
        <router-link to="/" class="brand" aria-label="Rentz Score, home">
          <img class="mark" src="/logo-mark.svg" width="32" height="32" alt="" />
          <span class="brand-name" aria-hidden="true">Rentz Score</span>
        </router-link>
        <nav class="top-tabs" aria-label="Main">
          <router-link
            v-for="tab in tabs"
            :key="tab.to"
            :to="tab.to"
            :class="{ 'is-current': tab.current }"
            :aria-current="tab.current ? 'page' : undefined"
          >
            {{ tab.label }}
          </router-link>
        </nav>
        <router-link
          to="/new-game"
          class="btn btn-primary new-game"
          :class="{ 'is-current': isNewGameRoute }"
          :aria-current="isNewGameRoute ? 'page' : undefined"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
            <path
              d="M12 5v14M5 12h14"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
            />
          </svg>
          New game
        </router-link>
      </div>
    </header>

    <main class="main">
      <router-view></router-view>
    </main>

    <nav class="tabbar" aria-label="Main">
      <router-link
        v-for="tab in allTabs"
        :key="tab.to"
        :to="tab.to"
        class="tabbar-item"
        :class="{ 'is-current': tab.current, 'is-action': tab.action }"
        :aria-current="tab.current ? 'page' : undefined"
      >
        <span class="tabbar-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="24" height="24">
            <path
              v-for="(d, i) in ICONS[tab.icon]"
              :key="i"
              :d="d"
              fill="none"
              stroke="currentColor"
              stroke-width="1.9"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
        <span class="tabbar-label">{{ tab.label }}</span>
      </router-link>
    </nav>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useGameStore } from './stores/game';

const route = useRoute();
const gameStore = useGameStore();

const ICONS = {
  home: ['M4 10.5 12 4l8 6.5V19a1 1 0 0 1-1 1h-4.5v-5.5h-5V20H5a1 1 0 0 1-1-1z'],
  game: [
    'M9.5 4h8A1.5 1.5 0 0 1 19 5.5v11a1.5 1.5 0 0 1-1.5 1.5h-8A1.5 1.5 0 0 1 8 16.5v-11A1.5 1.5 0 0 1 9.5 4z',
    'M5 7.5v11A1.5 1.5 0 0 0 6.5 20H14'
  ],
  history: ['M3.5 12a8.5 8.5 0 1 0 2.6-6.1L3.5 8.5', 'M3.5 4v4.5H8', 'M12 8v4.5l3 1.8'],
  new: ['M12 5v14', 'M5 12h14']
};

const isNewGameRoute = computed(
  () => route.name === 'new-game' || route.name === 'select-mini-games'
);

const tabs = computed(() =>
  [
    { to: '/', label: 'Home', icon: 'home', current: route.name === 'home' },
    gameStore.hasActiveGame && {
      to: '/game',
      label: 'Game',
      icon: 'game',
      current: route.name === 'game'
    },
    { to: '/history', label: 'History', icon: 'history', current: route.name === 'history' }
  ].filter(Boolean)
);

const allTabs = computed(() => [
  ...tabs.value,
  { to: '/new-game', label: 'New game', icon: 'new', current: isNewGameRoute.value, action: true }
]);

onMounted(() => gameStore.syncHistory());
</script>

<style>
.app {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
}

.main {
  flex: 1;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 16px calc(2rem + var(--tabbar-space));
}

.topbar {
  position: sticky;
  top: 0;
  z-index: 50;
  padding-top: env(safe-area-inset-top);
  background-color: var(--topbar);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--panel-edge);
}

.topbar-inner {
  height: 52px;
  padding: 0 16px;
  display: flex;
  align-items: center;
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--on-table);
  text-decoration: none;
}

.mark {
  flex: none;
}

.brand-name {
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 1.1875rem;
  letter-spacing: 0.005em;
  white-space: nowrap;
}

.top-tabs,
.topbar .new-game {
  display: none;
}

.tabbar {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 50;
  display: flex;
  padding: 6px 8px env(safe-area-inset-bottom);
  background-color: var(--topbar);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-top: 1px solid var(--panel-edge);
}

.tabbar-item {
  flex: 1;
  min-width: 0;
  min-height: 56px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  border-radius: 12px;
  color: var(--on-table-muted);
  text-decoration: none;
  font-size: 0.75rem;
  font-weight: 600;
  transition: color 0.15s;
}

.tabbar-icon {
  width: 52px;
  height: 30px;
  display: grid;
  place-items: center;
  border-radius: 999px;
  transition: background-color 0.15s;
}

.tabbar-item.is-current {
  color: var(--on-table);
}

.tabbar-item.is-current .tabbar-icon {
  background-color: var(--table-raised);
}

.tabbar-item.is-action .tabbar-icon {
  background-color: var(--accent);
  color: var(--on-accent);
}

.tabbar-label {
  white-space: nowrap;
}

@media (min-width: 768px) {
  .tabbar {
    display: none;
  }

  .topbar-inner {
    position: relative;
    max-width: 1200px;
    height: 64px;
    margin: 0 auto;
    padding: 0 32px;
  }

  .brand-name {
    font-size: 1.3125rem;
  }

  .top-tabs {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 2px;
    padding: 4px;
    border-radius: 999px;
    background-color: var(--panel-solid);
    border: 1px solid var(--panel-edge);
  }

  .top-tabs a {
    min-height: 34px;
    display: inline-flex;
    align-items: center;
    padding: 0 0.875rem;
    border-radius: 999px;
    color: var(--on-table-muted);
    text-decoration: none;
    font-weight: 600;
    font-size: 0.9375rem;
    transition:
      color 0.15s,
      background-color 0.15s;
  }

  .top-tabs a:hover {
    color: var(--on-table);
  }

  .top-tabs a.is-current {
    color: var(--on-table);
    background-color: var(--table-raised);
  }

  .topbar .new-game {
    display: inline-flex;
    margin-left: auto;
    min-height: 40px;
    padding: 0 1rem 0 0.875rem;
    border-radius: 999px;
    font-size: 0.9375rem;
  }

  .topbar .new-game.is-current {
    background-color: var(--table-raised);
    color: var(--on-table);
  }

  .main {
    padding: 2.5rem 32px 4rem;
  }
}
</style>
